import React, {
  forwardRef,
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import classNames from "classnames";

import { UIActionContext } from "../../../contexts/UIActionContext";

type InputType = "email" | "text" | "password" | "number" | "date";

type Props = {
  prefix?: ReactNode;
  postfix?: ReactNode;
  type: InputType;
  name?: string;
  placeholder?: string;
  note?: string;
  error?: string;
  success?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  viewOnly?: boolean;
  className?: string;
  wrapperClassName?: string;
  prefixClassName?: string;
  postfixClassName?: string;
  maxLength?: number;
  autoComplete?: "on" | "off" | "current-password" | "new-password";
  [restProps: string]: any;
  dataTest?: string;
  bindToContext?: boolean;
};

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      prefix = "",
      postfix = "",
      autoComplete = "on",
      type,
      name = "",
      placeholder = "",
      note = "",
      error = "",
      success = false,
      className = "",
      wrapperClassName = "",
      prefixClassName = "",
      postfixClassName = "",
      maxLength,
      onChange = (): void => undefined,
      onBlur = (): void => undefined,
      disabled = false,
      viewOnly = false,
      dataTest,
      bindToContext = false,
      ...restProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const context = useContext(UIActionContext);

    if (bindToContext && !context) {
      throw new Error(
        `FocusContext not defined for FocusProvider for "${name}" field`
      );
    }

    useImperativeHandle(ref, () => ({
      ...(inputRef.current as HTMLInputElement),
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    useEffect(() => {
      if (bindToContext) {
        context?.registerElement(name, inputRef);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, context?.registerElement, inputRef]);

    return (
      <div className={wrapperClassName}>
        <div className="relative">
          <input
            autoComplete={autoComplete}
            ref={inputRef}
            type={type}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            className={classNames(
              "input-base",
              {
                "border-gray-100 hover:border-gray-200 active:border-gray-500 focus-visible:border-gray-500":
                  !error && !success,
                "border-orange-500": !!error,
                "border-green-500": success,
                "bg-gray-50": disabled,
                "pr-14": !!postfix,
                "pl-10": !!prefix,
              },
              className
            )}
            disabled={disabled}
            readOnly={viewOnly}
            data-test={dataTest}
            {...restProps}
          />

          {!!postfix && (
            <span
              className={classNames(
                "absolute right-0 top-1/2 -translate-y-1/2 px-2 w-fit flex items-center",
                postfixClassName
              )}
            >
              {postfix}
            </span>
          )}

          {!!prefix && (
            <span
              className={classNames(
                "absolute left-0 top-1/2 -translate-y-1/2 pl-6 w-10 flex items-center text-gray-200",
                prefixClassName
              )}
            >
              {prefix}
            </span>
          )}
        </div>

        {error && (
          <div id={`${name}-error`} className="theme-text-error text-sm mt-1">
            {error}
          </div>
        )}

        {!error && note && (
          <div className="theme-subheading text-sm text-sm mt-2 w-full flex">
            {note}
          </div>
        )}
      </div>
    );
  }
);
